'use client';

import { Button } from '@/components/originui/button';
import { Label } from '@/components/ui/label';
import { useCreateContactInvitationMutation } from '@/hooks/membership/mutations/use-create-contact-invitation.mutation';
import { MembershipRole } from '@/types/api/membership';
import { cn } from '@/utils/shared/Utils';
import { EmailsSchema } from '@/validators/emails.schema';
import { Tag, TagInput } from 'emblor';
import { LoaderCircleIcon, SendIcon } from 'lucide-react';
import { useId, useState } from 'react';

interface CreateContactInvitationsInputProps {
  role: MembershipRole;
}

export default function CreateContactInvitationsInput({ role }: CreateContactInvitationsInputProps) {
  const mutation = useCreateContactInvitationMutation();

  const id = useId();
  const [tags, setTags] = useState<Tag[]>([]);
  const [activeTagIndex, setActiveTagIndex] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSend = async () => {
    try {
      setErrorMessage(null);

      const emails = tags.map((tag) => tag.text);

      const result = EmailsSchema.safeParse(emails);

      if (!result.success) {
        const errorMessage = [...new Set(result.error.errors.map((e) => e.message))].join(', ');
        setErrorMessage(errorMessage);
        return;
      }

      mutation.mutate({ role, emails }, { onSuccess: () => setTags([]) });
    } catch (error) {
      console.error('Inbjudan misslyckades', error);
    }
  };

  return (
    <div className="*:not-first:mt-2 space-y-1">
      <Label className="font-normal" htmlFor={id}>
        Bjud in via e-post
      </Label>
      <div className="flex gap-2">
        <TagInput
          addTagsOnBlur
          id={id}
          tags={tags}
          setTags={(newTags: Tag[]) => {
            setTags(newTags);
          }}
          placeholder="Ange e-poster..."
          styleClasses={{
            inlineTagsContainer:
              'border-input rounded-md bg-background shadow-xs transition-[color,box-shadow] focus-within:border-violet-500 outline-none focus-within:ring-[3px] focus-within:ring-violet-500/50 p-1 gap-1',
            input: 'w-full min-w-[80px] shadow-none px-2 h-7',
            tag: {
              body: 'h-7 relative bg-background border border-input hover:bg-background rounded-md font-medium text-xs ps-2 pe-7',
              closeButton:
                'absolute -inset-y-px -end-px p-0 rounded-e-md flex size-7 transition-[color,box-shadow] outline-none focus-visible:border-violet-500 focus-visible:ring-violet-500/50 focus-visible:ring-[3px] text-muted-foreground/80 hover:text-foreground',
            },
          }}
          activeTagIndex={activeTagIndex}
          setActiveTagIndex={setActiveTagIndex}
        />
        <Button variant="outline" onClick={handleSend} disabled={mutation.isPending}>
          {mutation.isPending ? (
            <LoaderCircleIcon
              className={cn('animate-spin transition-opacity duration-200')}
              size={16}
              aria-hidden="true"
            />
          ) : (
            <SendIcon className={cn('transition-opacity duration-200')} size={16} aria-hidden="true" />
          )}
          Skicka
        </Button>
      </div>
      {errorMessage && (
        <p className="text-red-500 mt-2 text-xs" role="region" aria-live="polite">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
